import React, { useEffect, useMemo, useState } from "react";
import {
  Page,
  Card,
  Text,
  TextField,
  Button,
  InlineStack,
  BlockStack,
  Box,
  Badge,
} from "@shopify/polaris";
import { isPendingReturnOrder } from "../utils/rentalStatus";
import LicenseExpiredOverlay from "../components/LicenseExpiredOverlay";

const EYE_ICON = String.fromCodePoint(0x1F441);
const TABLE_TEMPLATE = "1.45fr 2.1fr 1.45fr 1fr 1.05fr 1fr 1fr 1fr 110px 110px";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function getBadgeTone(status) {
  if (status === "Return Requested") return "warning";
  if (status === "Return") return "success";
  if (status === "On Rent") return "attention";
  return "info";
}

function parseReturnQuantityFromText(text = "") {
  const matches = String(text || "").match(/\bx(\d+)\b/gi) || [];
  return matches.reduce((sum, match) => {
    const qty = Number(String(match || "").replace(/[^0-9]/g, ""));
    return sum + (Number.isFinite(qty) ? qty : 0);
  }, 0);
}

function getReturnQuantity(order = {}) {
  const explicit = Number(order?.returnRequestedQuantity || order?.returnQuantity || 0);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;

  const parsed = parseReturnQuantityFromText(order?.returnReason || "");
  if (parsed > 0) return parsed;

  if (String(order?.status || "").trim() === "Return Requested") {
    return 1;
  }

  return Math.max(0, Number(order?.quantity) || 0);
}

function getRemainingQuantity(order = {}) {
  const total = Math.max(0, Number(order?.quantity) || 0);
  const returnQty = Math.max(0, getReturnQuantity(order));
  return Math.max(0, total - returnQty);
}

export default function PendingApproval({ onNavigate, license }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [approving, setApproving] = useState(false);
  const [approvingOrderId, setApprovingOrderId] = useState("");
  const [toast, setToast] = useState({ visible: false, tone: "success", message: "" });
  const [licenseExpired, setLicenseExpired] = useState(false);
  const dismissedOrderIdsRef = React.useRef(new Set());

  const isLicenseFlaggedExpired =
    Boolean(license?.remainingDays !== undefined && Number(license.remainingDays) <= 0) ||
    Boolean(license?.plan_active === false);

  const isExpired = licenseExpired || isLicenseFlaggedExpired;

  useEffect(() => {
    if (!toast.visible) return undefined;
    const timer = setTimeout(() => {
      setToast((current) => ({ ...current, visible: false }));
    }, 3200);
    return () => clearTimeout(timer);
  }, [toast.visible]);

  function showToast(message, tone = "success") {
    setToast({
      visible: true,
      tone,
      message,
    });
  }

  async function loadOrders(activeRef = { current: true }) {
    if (activeRef.current) {
      setLoading(true);
      setError("");
    }

    try {
      const response = await fetch(`/api/admin/rental-orders?pendingOnly=1&_ts=${Date.now()}`, {
        credentials: "include",
        cache: "no-store",
      });
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403 || data?.isExpired || String(data?.error || "").toLowerCase().includes("license expired")) {
          setLicenseExpired(true);
        }
        throw new Error(data?.message || data?.error || "Failed to load rental orders");
      }

      if (!activeRef.current) return;

      const nextOrders = Array.isArray(data) ? data : [];
      const hiddenOrderIds = dismissedOrderIdsRef.current;
      setOrders(
        nextOrders.filter(
          (order) =>
            isPendingReturnOrder(order) &&
            !hiddenOrderIds.has(String(order?.orderId || "").trim()),
        ),
      );
    } catch (loadError) {
      if (!activeRef.current) return;
      const errMsg = loadError?.message || "Failed to load rental orders";
      if (errMsg.toLowerCase().includes("license expired") || errMsg.toLowerCase().includes("license")) {
        setLicenseExpired(true);
      }
      setError(errMsg);
    } finally {
      if (activeRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    const activeRef = { current: true };
    loadOrders(activeRef);
    return () => {
      activeRef.current = false;
    };
  }, []);

  const filteredOrders = useMemo(() => {
    const query = String(searchTerm || "").trim().toLowerCase();
    return orders.filter((order) => {
      return (
        !query ||
        String(order.orderId || "").toLowerCase().includes(query) ||
        String(order.orderLabel || "").toLowerCase().includes(query) ||
        String(order.productName || "").toLowerCase().includes(query) ||
        String(order.customerName || "").toLowerCase().includes(query)
      );
    });
  }, [orders, searchTerm]);

  const getDisplayStatus = (order = {}) =>
    isPendingReturnOrder(order) ? "Return Requested" : String(order?.status || "").trim();

  const approveReturn = async (orderIdInput = "") => {
    const orderId = String(orderIdInput || "").trim();
    if (!orderId || approving) return;

    setApproving(true);
    setApprovingOrderId(orderId);
    setError("");

    try {
      const response = await fetch(`/api/admin/rental-orders/${encodeURIComponent(orderId)}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        cache: "no-store",
        body: JSON.stringify({ status: "Return" }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to approve return request");
      }

      dismissedOrderIdsRef.current.add(orderId);
      setOrders((current) => current.filter((order) => String(order?.orderId || "").trim() !== orderId));
      await loadOrders();
      showToast(`Approved return request for order ${orderId}.`, "success");
    } catch (approveError) {
      setError(approveError?.message || "Failed to approve return request");
    } finally {
      setApproving(false);
      setApprovingOrderId("");
    }
  };

  const rejectReturn = async (orderIdInput = "") => {
    const orderId = String(orderIdInput || "").trim();
    if (!orderId || approving) return;

    setApproving(true);
    setApprovingOrderId(orderId);
    setError("");

    try {
      const response = await fetch(`/api/admin/rental-orders/${encodeURIComponent(orderId)}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        cache: "no-store",
        body: JSON.stringify({ status: "Rejected" }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to reject return request");
      }

      dismissedOrderIdsRef.current.add(orderId);
      setOrders((current) => current.filter((order) => String(order?.orderId || "").trim() !== orderId));
      await loadOrders();
      showToast(`Rejected return request for order ${orderId}.`, "success");
    } catch (rejectError) {
      setError(rejectError?.message || "Failed to reject return request");
    } finally {
      setApproving(false);
      setApprovingOrderId("");
    }
  };

  return (
    <Page title="Pending Approval" fullWidth>
      {isExpired && (
        <LicenseExpiredOverlay
          onNavigate={onNavigate}
          license={license}
          customMessage="Your NetScore Rental Management license has expired. The pending approval queue is currently paused."
        />
      )}
      <div
        style={{
          filter: isExpired ? "blur(6px)" : "none",
          pointerEvents: isExpired ? "none" : "auto",
          userSelect: isExpired ? "none" : "auto",
          transition: "filter 0.2s ease",
        }}
      >
        {toast.visible && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "fixed",
              top: "24px",
              right: "24px",
              zIndex: 9999,
              minWidth: "320px",
              maxWidth: "420px",
              padding: "14px 16px",
              borderRadius: "16px",
              background:
                toast.tone === "success"
                  ? "linear-gradient(135deg, #0f766e 0%, #0f172a 100%)"
                  : "linear-gradient(135deg, #991b1b 0%, #111827 100%)",
              color: "#ffffff",
              boxShadow: "0 18px 40px rgba(15, 23, 42, 0.28)",
              border: "1px solid rgba(255, 255, 255, 0.14)",
            }}
          >
            <BlockStack gap="100">
              <Text fontWeight="medium">{toast.tone === "success" ? "Success" : "Update"}</Text>
              <Text>{toast.message}</Text>
            </BlockStack>
          </div>
        )}
        {onNavigate && (
          <Box paddingBlockEnd="400">
            <Button onClick={() => onNavigate("dashboard")}>Back</Button>
          </Box>
        )}

        <BlockStack gap="400">
          <Card>
            <Box padding="400">
              <BlockStack gap="250">
                <Text variant="headingMd" as="h2">
                  Pending Return Requests
                </Text>
                <InlineStack align="space-between" blockAlign="center" wrap>
                  <Box minWidth="280px">
                    <TextField
                      label=" "
                      value={searchTerm}
                      onChange={setSearchTerm}
                      autoComplete="off"
                      placeholder="Search Order / Product / Customer"
                    />
                  </Box>
                  <InlineStack gap="200" wrap={false}>
                    <Button onClick={() => loadOrders()}>Refresh</Button>
                  </InlineStack>
                </InlineStack>
                <InlineStack gap="200" wrap>
                  <Badge tone="warning">{filteredOrders.length} Pending Requests</Badge>
                </InlineStack>
                {loading && <Text tone="subdued">Loading pending requests...</Text>}
                {!loading && !searchTerm && !orders.length && (
                  <Text tone="subdued">No return requests awaiting approval.</Text>
                )}
                {!loading && !!searchTerm && !filteredOrders.length && (
                  <Text tone="subdued">No pending requests match your search.</Text>
                )}
                {!!error && !isExpired && <Text tone="critical">{error}</Text>}

                <Box>
                  <BlockStack gap="0">
                    <Box
                      padding="200"
                      background="bg-surface-secondary"
                      borderRadius="100"
                    >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: TABLE_TEMPLATE,
                            gap: "20px",
                            alignItems: "center",
                            minWidth: "1180px",
                          }}
                        >
                        <Text fontWeight="medium">Order ID</Text>
                        <Text fontWeight="medium">Product Name</Text>
                        <Text fontWeight="medium">Customer</Text>
                        <Text fontWeight="medium">Return Qty</Text>
                        <Text fontWeight="medium">Remaining Qty</Text>
                        <Text fontWeight="medium">Start Date</Text>
                        <Text fontWeight="medium">End Date</Text>
                        <Text fontWeight="medium">Status</Text>
                        <Text fontWeight="medium">Approve</Text>
                        <Text fontWeight="medium">Reject</Text>
                      </div>
                    </Box>

                    {!loading &&
                      filteredOrders.map((order) => (
                        <Box
                          key={order.orderId}
                          padding="200"
                          borderBlockEndWidth="025"
                          borderColor="border-subdued"
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: TABLE_TEMPLATE,
                              gap: "20px",
                              alignItems: "center",
                              minWidth: "1180px",
                            }}
                          >
                            <Text>{order.orderLabel || `#${order.orderId}`}</Text>
                            <Text>{order.productName || "-"}</Text>
                            <Text>{order.customerName || "Guest"}</Text>
                            <Text>{getReturnQuantity(order)}</Text>
                            <Text>{getRemainingQuantity(order)}</Text>
                            <Text>{formatDate(order.startDate)}</Text>
                            <Text>{formatDate(order.endDate)}</Text>
                            <Box>
                              <Badge tone={getBadgeTone(getDisplayStatus(order))}>
                                {getDisplayStatus(order)}
                              </Badge>
                            </Box>
                            <Box>
                              {isPendingReturnOrder(order) ? (
                                <Button
                                  size="slim"
                                  variant="primary"
                                  loading={approving && approvingOrderId === String(order.orderId || "").trim()}
                                  onClick={() => approveReturn(order.orderId)}
                                >
                                  Approve
                                </Button>
                              ) : (
                                <Text tone="subdued">-</Text>
                              )}
                            </Box>
                            <Box>
                              {isPendingReturnOrder(order) ? (
                                <Button
                                  size="slim"
                                  loading={approving && approvingOrderId === String(order.orderId || "").trim()}
                                  onClick={() => rejectReturn(order.orderId)}
                                >
                                  Reject
                                </Button>
                              ) : (
                                <Text tone="subdued">-</Text>
                              )}
                            </Box>
                          </div>
                        </Box>
                      ))}

                    {!loading && filteredOrders.length === 0 && (
                      <Box padding="200">
                        <Text tone="subdued">No pending return requests available.</Text>
                      </Box>
                    )}
                  </BlockStack>
                </Box>
              </BlockStack>
            </Box>
          </Card>
        </BlockStack>
      </div>
    </Page>
  );
}
